import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Typography, Space, Tag, Badge, Button, Row, Col, Card, Table, Progress,
    Empty, Spin, Divider, Breadcrumb, message, Alert, Popconfirm, Form,
} from 'antd';
import {
    RobotOutlined, AimOutlined, PauseCircleOutlined, PlayCircleOutlined,
    StopOutlined, CameraOutlined, HomeOutlined, SendOutlined,
    ThunderboltOutlined, WifiOutlined, CompassOutlined, DashboardOutlined,
    EnvironmentOutlined, ClockCircleOutlined, VideoCameraOutlined,
    ArrowLeftOutlined, CloseCircleOutlined, SaveOutlined, SettingOutlined,
    ExportOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import Map, { Marker, Source, Layer } from 'react-map-gl';
import type { MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import type { DeviceItem, MissionItem } from '../components/Operations/missionModels';
import { statusColorMap } from '../components/Operations/missionModels';
import type { DeviceCommandItem, DeviceTelemetryItem, DeviceVideoStreamItem } from '../components/Operations/operationModels';
import { commandStatusColor } from '../components/Operations/operationModels';
import MjpegCameraFeed from '../components/Operations/MjpegCameraFeed';
import ModuleFormList from '../components/Configuration/ModuleFormList';
import { deepMergeConfig } from '../components/Configuration/ModuleConfigEditor';
import type { ModuleDefinitionItem } from '../components/Configuration/types';
import {
    getActiveMode,
    getBatteryPercent,
    getLocalInterfaceUrl,
    getLocalizationLabel,
    getManualOverride,
    getMissionProgress,
    getModuleRuntimeRows,
    getStatusSnapshot,
    getUnitStatus,
} from '../components/Operations/deviceStatusSnapshot';

const { Title, Text } = Typography;

const MAPBOX_TOKEN =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';
const LIVE_GPS_MAP_HEIGHT = 'clamp(380px, 31vw, 480px)';

/* ─── Types ─── */

interface TelemetryPosition {
    id?: number;
    lat: number;
    lng: number;
    heading?: number;
    speed?: number;
    altitude?: number;
    battery?: number;
    timestamp: string;
}

const telemetryToPosition = (telemetry: DeviceTelemetryItem): TelemetryPosition | null => {
    if (telemetry.telemetryType !== 'position' && telemetry.telemetryType !== 'heartbeat') return null;
    const lat = Number(telemetry.payload?.lat ?? telemetry.payload?.latitude);
    const lng = Number(telemetry.payload?.lng ?? telemetry.payload?.lon ?? telemetry.payload?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) return null;

    const optionalNumber = (value: unknown): number | undefined => {
        if (value === null || value === undefined || value === '') return undefined;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    };

    return {
        id: telemetry.id,
        lat,
        lng,
        heading: optionalNumber(telemetry.payload?.heading ?? telemetry.payload?.yaw),
        speed: optionalNumber(telemetry.payload?.speed ?? telemetry.payload?.groundSpeed),
        altitude: optionalNumber(telemetry.payload?.altitude ?? telemetry.payload?.alt),
        battery: optionalNumber(telemetry.payload?.battery ?? telemetry.payload?.batteryPercent),
        timestamp: telemetry.createdAt || telemetry.deviceTimestamp || '',
    };
};

const mergeTelemetryPositions = (
    incoming: TelemetryPosition[],
    current: TelemetryPosition[]
): TelemetryPosition[] => {
    const seen = new Set<string>();
    const merged: TelemetryPosition[] = [];
    [...incoming, ...current].forEach((position) => {
        const key = position.id
            ? `id:${position.id}`
            : `${position.timestamp}:${position.lat}:${position.lng}`;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(position);
    });
    return merged
        .sort((a, b) => Date.parse(b.timestamp || '') - Date.parse(a.timestamp || ''))
        .slice(0, 50);
};

/* ─── Robot marker SVG (arrow showing heading) ─── */

const RobotMarkerSVG: React.FC<{ heading: number; online: boolean }> = ({ heading, online }) => (
    <div style={{ transform: `rotate(${heading}deg)`, transition: 'transform 0.5s ease' }}>
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="14" fill={online ? '#52c41a' : '#8c8c8c'} fillOpacity="0.25" stroke={online ? '#52c41a' : '#8c8c8c'} strokeWidth="2" />
            <polygon points="16,4 24,24 16,19 8,24" fill={online ? '#52c41a' : '#8c8c8c'} />
        </svg>
    </div>
);

/* ─── Camera stream placeholder ─── */

const CameraSlot: React.FC<{ label: string; index: number }> = ({ label, index }) => (
    <div style={{
        background: '#1a1a2e',
        borderRadius: 8,
        aspectRatio: '16/9',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #303050',
        position: 'relative',
        overflow: 'hidden',
    }}>
        <VideoCameraOutlined style={{ fontSize: 28, color: '#555' }} />
        <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
            {label}
        </Text>
        <Text type="secondary" style={{ fontSize: 9, marginTop: 2 }}>
            Connect stream to view
        </Text>
        <div style={{
            position: 'absolute', top: 6, left: 8,
            background: 'rgba(0,0,0,0.6)', borderRadius: 4, padding: '1px 6px',
        }}>
            <Text style={{ fontSize: 9, color: '#aaa' }}>CAM {index + 1}</Text>
        </div>
    </div>
);

const stringifyJson = (value: any): string => {
    if (!value || typeof value !== 'object') return '';
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return '';
    }
};

const parseJsonObjectText = (value: unknown, label: string): Record<string, any> | null => {
    if (typeof value !== 'string' || value.trim() === '') {
        return null;
    }
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error(`${label} must be a JSON object`);
    }
    return parsed;
};

const findModuleDefinition = (definitions: ModuleDefinitionItem[], id: unknown): ModuleDefinitionItem | undefined => {
    if (id === undefined || id === null || id === '') return undefined;
    const numericId = Number(id);
    return definitions.find((definition) => Number(definition.id) === numericId);
};

const mergeModuleConfigDefaults = (definitions: ModuleDefinitionItem[], moduleDefinitionId: unknown, config: unknown, embeddedDefinition?: any): Record<string, any> => {
    const definition = findModuleDefinition(definitions, moduleDefinitionId) || embeddedDefinition;
    const currentConfig = config && typeof config === 'object' && !Array.isArray(config) ? config : {};
    return definition?.defaultConfig ? deepMergeConfig(definition.defaultConfig, currentConfig) : currentConfig as Record<string, any>;
};

/* ─── Main View ─── */

const DeviceDetailView: React.FC = () => {
    const { deviceId } = useParams<{ deviceId: string }>();
    const navigate = useNavigate();

    const [device, setDevice] = useState<DeviceItem | null>(null);
    const [loadingDevice, setLoadingDevice] = useState(true);
    const [telemetry, setTelemetry] = useState<TelemetryPosition[]>([]);
    const [commands, setCommands] = useState<DeviceCommandItem[]>([]);
    const [videoStreams, setVideoStreams] = useState<DeviceVideoStreamItem[]>([]);
    const [moduleDefinitions, setModuleDefinitions] = useState<ModuleDefinitionItem[]>([]);
    const [savingModules, setSavingModules] = useState(false);
    const [activeMission, setActiveMission] = useState<MissionItem | null>(null);
    const [loadingMission, setLoadingMission] = useState(false);
    const [sendingCommand, setSendingCommand] = useState(false);
    const [pendingAction, setPendingAction] = useState<string | null>(null);
    const prevMissionStatus = useRef<string | null>(null);
    const mapRef = useRef<MapRef | null>(null);
    const missionLoadedOnce = useRef(false);
    const [moduleForm] = Form.useForm();

    const isOnline = device?.lastHeartbeat
        ? Date.now() - new Date(device.lastHeartbeat).getTime() < 60000
        : false;

    /* ─── Fetch device ─── */
    const loadDevice = useCallback(async () => {
        if (!deviceId) return;
        try {
            const res = await axios.get(`${ENDPOINTS.GET_DEVICES}/${deviceId}`, { withCredentials: true });
            const dev = res.data?.data ?? res.data;
            setDevice(dev);
        } catch {
            // try from list
            try {
                const res = await axios.get(ENDPOINTS.GET_DEVICES, { withCredentials: true });
                const devList: DeviceItem[] = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
                const found = devList.find((d) => String(d.id) === deviceId);
                setDevice(found || null);
            } catch {
                setDevice(null);
            }
        } finally {
            setLoadingDevice(false);
        }
    }, [deviceId]);

    const loadModuleDefinitions = useCallback(async () => {
        try {
            const res = await axios.get(ENDPOINTS.MODULE_DEFINITIONS, { withCredentials: true });
            const rows = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
            setModuleDefinitions(rows);
        } catch {
            setModuleDefinitions([]);
        }
    }, []);

    const loadVideoStreams = useCallback(async () => {
        if (!deviceId) return;
        try {
            const res = await axios.get(ENDPOINTS.DEVICE_VIDEO_STREAMS(deviceId), { withCredentials: true });
            const streams: DeviceVideoStreamItem[] = Array.isArray(res.data?.data)
                ? res.data.data
                : Array.isArray(res.data) ? res.data : [];
            setVideoStreams(streams);
        } catch {
            setVideoStreams([]);
        }
    }, [deviceId]);

    /* ─── Fetch telemetry ─── */
    const loadTelemetry = useCallback(async () => {
        if (!deviceId) return;
        try {
            const res = await axios.get(ENDPOINTS.DEVICE_TELEMETRY, {
                withCredentials: true,
                params: { deviceId, limit: 50 },
            });
            const items: DeviceTelemetryItem[] = Array.isArray(res.data?.data)
                ? res.data.data
                : Array.isArray(res.data) ? res.data : [];

            const positions = items
                .map(telemetryToPosition)
                .filter((position): position is TelemetryPosition => position !== null);

            setTelemetry((current) => mergeTelemetryPositions(positions, current));
        } catch {
            // silently ignore
        }
    }, [deviceId]);

    /* ─── Fetch commands ─── */
    const loadCommands = useCallback(async () => {
        if (!deviceId) return;
        try {
            const res = await axios.get(ENDPOINTS.DEVICE_COMMANDS, {
                withCredentials: true,
                params: { deviceId, limit: 10 },
            });
            const cmds: DeviceCommandItem[] = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
            setCommands(cmds);
        } catch {
            // silently ignore
        }
    }, [deviceId]);

    /* ─── Fetch active mission ─── */
    const loadActiveMission = useCallback(async () => {
        if (!deviceId) return;
        // Only show spinner on the very first load, not during polling
        if (!missionLoadedOnce.current) {
            setLoadingMission(true);
        }
        try {
            const res = await axios.get(ENDPOINTS.MISSIONS, {
                withCredentials: true,
                params: { deviceId, status: 'running' },
            });
            const missions: MissionItem[] = Array.isArray(res.data?.data)
                ? res.data.data
                : Array.isArray(res.data) ? res.data : [];
            setActiveMission(missions[0] || null);
        } catch {
            setActiveMission(null);
        } finally {
            missionLoadedOnce.current = true;
            setLoadingMission(false);
        }
    }, [deviceId]);

    /* ─── Initial load + polling ─── */
    useEffect(() => {
        void loadDevice();
        void loadModuleDefinitions();
    }, [loadDevice, loadModuleDefinitions]);

    useEffect(() => {
        moduleForm.setFieldsValue({
            modules: (device?.modules || []).map((module) => {
                const moduleDefinitionId = module.moduleDefinitionId ?? module.moduleDefinition?.id ?? undefined;
                return {
                    moduleDefinitionId,
                    moduleDefinition: module.moduleDefinition ?? undefined,
                    name: module.name ?? undefined,
                    instanceKey: module.instanceKey ?? undefined,
                    status: module.status || 'installed',
                    isDefault: module.isDefault ?? false,
                    config: mergeModuleConfigDefaults(moduleDefinitions, moduleDefinitionId, module.config, module.moduleDefinition),
                    configText: stringifyJson(module.config),
                    metadataText: stringifyJson(module.metadata),
                };
            }),
        });
    }, [device, moduleDefinitions, moduleForm]);

    useEffect(() => {
        if (!device?.id) return;
        void loadTelemetry();
        void loadCommands();
        void loadVideoStreams();
        void loadActiveMission();
        void loadDevice();
        const interval = setInterval(() => {
            void loadCommands();
            void loadVideoStreams();
            void loadActiveMission();
            void loadDevice();
        }, 5000);
        return () => clearInterval(interval);
    }, [device?.id, loadTelemetry, loadCommands, loadVideoStreams, loadActiveMission, loadDevice]);

    /* ─── Live telemetry follows the robot sender cadence ─── */
    useEffect(() => {
        if (!device?.id) return;
        const streamUrl = new URL(ENDPOINTS.DEVICE_TELEMETRY_STREAM);
        streamUrl.searchParams.set('deviceId', String(device.id));
        const stream = new EventSource(streamUrl.toString(), { withCredentials: true });
        const handleTelemetry = (event: Event) => {
            try {
                const telemetryEvent = event as MessageEvent<string>;
                const item = JSON.parse(telemetryEvent.data) as DeviceTelemetryItem;
                const position = telemetryToPosition(item);
                if (position) {
                    setTelemetry((current) => mergeTelemetryPositions([position], current));
                }
            } catch {
                // Ignore one malformed event; EventSource remains connected.
            }
        };
        stream.addEventListener('telemetry', handleTelemetry);
        stream.onopen = () => {
            // Recover events that may have arrived while the browser stream
            // was disconnected, without imposing a recurring poll interval.
            void loadTelemetry();
        };
        return () => {
            stream.removeEventListener('telemetry', handleTelemetry);
            stream.close();
        };
    }, [device?.id, loadTelemetry]);

    /* ─── Auto-center map on latest position ─── */
    useEffect(() => {
        if (telemetry.length > 0 && mapRef.current) {
            const latest = telemetry[0];
            mapRef.current.jumpTo({ center: [latest.lng, latest.lat], zoom: 18 });
        }
    }, [telemetry]);

    /* ─── Pending action labels ─── */
    const pendingActionLabels: Record<string, { message: string; type: 'warning' | 'error' | 'info' }> = {
        pause_mission:   { message: '⏸ Pause command sent — waiting for robot to confirm…', type: 'warning' },
        emergency_stop:  { message: '🛑 Emergency stop sent — waiting for robot to confirm…', type: 'error' },
        resume_mission:  { message: '▶ Resume command sent — waiting for robot to confirm…', type: 'info' },
        abort_mission:   { message: 'Mission abort requested — waiting for robot to confirm…', type: 'error' },
        return_home:     { message: '🏠 Return home sent — waiting for robot to confirm…', type: 'info' },
    };

    /* ─── Auto-clear pending action when mission status changes ─── */
    useEffect(() => {
        const currentStatus = activeMission?.status ?? null;
        if (pendingAction && prevMissionStatus.current !== null && currentStatus !== prevMissionStatus.current) {
            // Mission status changed — robot has confirmed
            setPendingAction(null);
        }
        // Also clear if mission disappeared (e.g. cancelled/completed)
        if (pendingAction && prevMissionStatus.current !== null && !activeMission) {
            setPendingAction(null);
        }
        prevMissionStatus.current = currentStatus;
    }, [activeMission, pendingAction]);

    /* ─── Send command ─── */
    const sendCommand = async (
        commandType: string,
        payload?: Record<string, any> | null,
        context?: { missionId?: number | null; operationId?: number | null }
    ) => {
        if (!device) return;
        setSendingCommand(true);
        try {
            await axios.post(ENDPOINTS.DEVICE_COMMANDS, {
                deviceId: device.id,
                commandType,
                payload: payload || undefined,
                missionId: context?.missionId ?? payload?.missionId ?? undefined,
                operationId: context?.operationId ?? payload?.operationId ?? undefined,
            }, { withCredentials: true });
            message.success(`${commandType} sent to ${device.label}`);
            // Set pending action for mission-affecting commands
            if (pendingActionLabels[commandType]) {
                setPendingAction(commandType);
            }
            void loadCommands();
            void loadDevice();
        } catch (err: any) {
            message.error(err?.response?.data?.message || 'Failed to send command');
        } finally {
            setSendingCommand(false);
        }
    };

    const saveModules = async () => {
        if (!device) return;
        try {
            const values = await moduleForm.validateFields();
            const modules = Array.isArray(values.modules)
                ? values.modules.map((item: any, index: number) => ({
                    moduleDefinitionId: item?.moduleDefinitionId ?? null,
                    name: item?.name ? String(item.name).trim() : null,
                    instanceKey: item?.instanceKey ? String(item.instanceKey).trim() : null,
                    status: item?.status || 'installed',
                    isDefault: item?.isDefault ?? false,
                    config: item?.config && typeof item.config === 'object' && !Array.isArray(item.config)
                        ? mergeModuleConfigDefaults(moduleDefinitions, item?.moduleDefinitionId, item.config, item?.moduleDefinition)
                        : parseJsonObjectText(item?.configText, `Module ${index + 1} config`),
                    metadata: parseJsonObjectText(item?.metadataText, `Module ${index + 1} metadata`),
                }))
                : [];
            setSavingModules(true);
            await axios.put(`${ENDPOINTS.GET_DEVICES}/${device.id}/modules`, { modules }, { withCredentials: true });
            message.success('Device module configuration saved');
            await loadDevice();
        } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error?.response?.data?.message || error?.message || 'Failed to save modules');
        } finally {
            setSavingModules(false);
        }
    };

    /* ─── Loading state ─── */
    if (loadingDevice) {
        return <Spin style={{ display: 'block', textAlign: 'center', padding: 80 }} size="large" />;
    }

    if (!device) {
        return (
            <div style={{ padding: 24 }}>
                <Empty description="Device not found">
                    <Button type="primary" onClick={() => navigate('/operations')}>
                        Back to Operations
                    </Button>
                </Empty>
            </div>
        );
    }

    const latest = telemetry[0] || null;
    const statusSnapshot = getStatusSnapshot(device);
    const localInterfaceUrl = getLocalInterfaceUrl(statusSnapshot);
    const stagedMissions = Array.isArray(statusSnapshot?.stagedMissions)
        ? statusSnapshot?.stagedMissions || []
        : [];
    const battery = getBatteryPercent(statusSnapshot, latest?.battery);
    const unitStatus = getUnitStatus(device);
    const activeMode = getActiveMode(statusSnapshot);
    const manualOverride = getManualOverride(statusSnapshot);
    const localizationLabel = getLocalizationLabel(statusSnapshot);
    const moduleStatusRows = getModuleRuntimeRows(statusSnapshot, device.modules);
    const missionControlState = String(
        statusSnapshot?.activeMission?.state
        || statusSnapshot?.activeMission?.status
        || statusSnapshot?.mission?.state
        || activeMission?.status
        || ''
    ).trim().toLowerCase();
    const missionSnapshotHasIdentity = Boolean(
        statusSnapshot?.mission?.missionId
        || statusSnapshot?.mission?.operationId
        || statusSnapshot?.mission?.operationMissionId
    );
    const missionTerminalStates = ['completed', 'complete', 'done', 'failed', 'aborted', 'cancelled', 'canceled'];
    const missionIdleStates = ['idle', 'staged', 'queued', 'none', 'unknown'];
    const missionIsTerminal = missionTerminalStates.includes(missionControlState);
    const missionStateImpliesRuntime = Boolean(missionControlState)
        && !missionTerminalStates.includes(missionControlState)
        && !missionIdleStates.includes(missionControlState);
    const hasMissionControlContext = Boolean(
        activeMission
        || statusSnapshot?.activeMission
        || (missionSnapshotHasIdentity && missionStateImpliesRuntime)
    );
    const missionIsPaused = hasMissionControlContext && ['paused', 'pause', 'pausing', 'suspended'].includes(missionControlState);
    const canPauseMission = hasMissionControlContext && !missionIsPaused && !missionIsTerminal;
    const canResumeMission = missionIsPaused && !missionIsTerminal;
    const canCancelMission = hasMissionControlContext && !missionIsTerminal;
    const vehicleClass = device.deviceType?.vehicleProfile?.vehicleClass
        || device.deviceType?.vehicleCapabilities?.vehicleClass
        || device.deviceType?.commandProtocol
        || 'device';
    const missionDetailPath = (missionId: unknown) => {
        const parsed = Number(missionId);
        if (!Number.isFinite(parsed) || parsed <= 0) return '';
        return `/operations/missions/${parsed}?from=device&deviceId=${deviceId}`;
    };

    // Trail line GeoJSON
    const trailGeoJson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: telemetry.length >= 2
            ? [{
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: telemetry.map((p) => [p.lng, p.lat]),
                },
            }]
            : [],
    };

    // Mission progress
    const snapshotMissionProgress = getMissionProgress(statusSnapshot);
    const missionProgress = snapshotMissionProgress ?? (activeMission?.routeSummary
        ? Math.round(((activeMission.measurementCount || 0) / Math.max(activeMission.routeSummary.pointCount, 1)) * 100)
        : 0);

    return (
        <div style={{ padding: '0 0 24px 0' }}>
            {/* ─── Header ─── */}
            <Breadcrumb style={{ marginBottom: 12 }}>
                <Breadcrumb.Item>
                    <a onClick={() => navigate('/operations')}>Operations</a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <a onClick={() => navigate('/operations')}>Device Fleet</a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>{device.label}</Breadcrumb.Item>
            </Breadcrumb>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/operations')}
                    type="text"
                />
                <Badge status={isOnline ? 'success' : 'default'} />
                <RobotOutlined style={{ fontSize: 22 }} />
                <Title level={4} style={{ margin: 0 }}>{device.label}</Title>
                <Tag color={isOnline ? 'green' : 'default'}>{isOnline ? 'Online' : 'Offline'}</Tag>
                <Tag color="blue">{vehicleClass}</Tag>
                {localInterfaceUrl && (
                    <Button
                        size="small"
                        icon={<ExportOutlined />}
                        onClick={() => window.open(localInterfaceUrl, '_blank', 'noopener,noreferrer')}
                    >
                        Robot interface
                    </Button>
                )}
            </div>

            {/* ─── Row 1: Camera feeds + GPS Map ─── */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card size="small" title={<><VideoCameraOutlined /> Camera Feeds</>} bodyStyle={{ padding: 8 }}>
                        {videoStreams.length > 0 ? (
                            <Row gutter={[8, 8]}>
                                {videoStreams.map((stream) => (
                                    <Col span={12} key={stream.cameraKey}>
                                        <MjpegCameraFeed stream={stream} />
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            <Empty description="No video streams configured" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card
                        size="small"
                        title={<><EnvironmentOutlined /> Live GPS Location</>}
                        bodyStyle={{ padding: 0, height: LIVE_GPS_MAP_HEIGHT }}
                        extra={latest && (
                            <Text type="secondary" style={{ fontSize: 10 }}>
                                <CompassOutlined /> {latest.heading?.toFixed(0) ?? '—'}°
                            </Text>
                        )}
                    >
                        {latest ? (
                            <Map
                                ref={mapRef}
                                mapboxAccessToken={MAPBOX_TOKEN}
                                initialViewState={{
                                    latitude: latest.lat,
                                    longitude: latest.lng,
                                    zoom: 18,
                                }}
                                style={{ width: '100%', height: LIVE_GPS_MAP_HEIGHT }}
                                mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                            >
                                {/* Trail line */}
                                <Source id="trail" type="geojson" data={trailGeoJson}>
                                    <Layer
                                        id="trail-line"
                                        type="line"
                                        paint={{
                                            'line-color': '#1890ff',
                                            'line-width': 3,
                                            'line-opacity': 0.7,
                                            'line-dasharray': [2, 2],
                                        }}
                                    />
                                </Source>
                                {/* Robot marker */}
                                <Marker latitude={latest.lat} longitude={latest.lng} anchor="center">
                                    <RobotMarkerSVG heading={latest.heading ?? 0} online={isOnline} />
                                </Marker>
                            </Map>
                        ) : (
                            <div style={{ height: LIVE_GPS_MAP_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Empty description="No GPS data yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            </div>
                        )}
                    </Card>
                    {latest && (
                        <Space style={{ marginTop: 6, width: '100%', justifyContent: 'space-between' }} wrap>
                            <Text type="secondary" style={{ fontSize: 10 }}>
                                <EnvironmentOutlined /> {latest.lat.toFixed(6)}, {latest.lng.toFixed(6)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 10 }}>
                                <DashboardOutlined /> {latest.speed?.toFixed(1) ?? '—'} m/s
                            </Text>
                            <Text type="secondary" style={{ fontSize: 10 }}>
                                <CompassOutlined /> Heading: {latest.heading?.toFixed(0) ?? '—'}°
                            </Text>
                        </Space>
                    )}
                </Col>
            </Row>

            <Divider style={{ margin: '16px 0' }} />

            {/* ─── Row 2: Active Mission ─── */}
            <Card
                size="small"
                title={<><AimOutlined /> Active Mission</>}
                style={{ marginBottom: 16 }}
                bodyStyle={{ padding: 12, minHeight: 48 }}
            >
                {pendingAction && pendingActionLabels[pendingAction] && (
                    <Alert
                        message={pendingActionLabels[pendingAction].message}
                        type={pendingActionLabels[pendingAction].type}
                        showIcon
                        banner
                        style={{ marginBottom: activeMission ? 8 : 0, borderRadius: 4 }}
                    />
                )}
                {loadingMission ? (
                    <Spin size="small" />
                ) : activeMission ? (
                    <Row gutter={[16, 8]} align="middle">
                        <Col xs={24} sm={12}>
                            <Space direction="vertical" size={2}>
                                <Text strong>{activeMission.name}</Text>
                                <Space size={4}>
                                    <Tag color={statusColorMap[activeMission.status] || 'default'}>
                                        {activeMission.status}
                                    </Tag>
                                    {activeMission.missionType && (
                                        <Tag>{activeMission.missionType}</Tag>
                                    )}
                                </Space>
                                {activeMission.area && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        Area: {activeMission.area.name}
                                    </Text>
                                )}
                                {activeMission.pattern && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        Pattern: {activeMission.pattern.name}
                                    </Text>
                                )}
                            </Space>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <div>
                                    <Text style={{ fontSize: 11 }}>Progress</Text>
                                    <Progress
                                        percent={missionProgress}
                                        size="small"
                                        status={activeMission.status === 'running' ? 'active' : 'normal'}
                                    />
                                </div>
                                {activeMission.routeSummary && (
                                    <Text type="secondary" style={{ fontSize: 10 }}>
                                        {activeMission.measurementCount || 0}/{activeMission.routeSummary.pointCount} waypoints
                                        {' · '}{activeMission.routeSummary.totalDistanceKm?.toFixed(2)} km
                                        {' · ~'}{activeMission.routeSummary.estimatedDurationMinutes?.toFixed(0)} min
                                    </Text>
                                )}
                                {activeMission.startedAt && (
                                    <Text type="secondary" style={{ fontSize: 10 }}>
                                        <ClockCircleOutlined /> Started: {new Date(activeMission.startedAt).toLocaleTimeString()}
                                    </Text>
                                )}
                            </Space>
                        </Col>
                    </Row>
                ) : (
                    <Text type="secondary">No active mission — device is idle</Text>
                )}
            </Card>

            {/* ─── Row 3: Staged Missions ─── */}
            <Card
                size="small"
                title={<><SendOutlined /> Staged Missions</>}
                style={{ marginBottom: 16 }}
                extra={statusSnapshot?.stagedMissionsUpdatedAt && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        Updated {new Date(statusSnapshot.stagedMissionsUpdatedAt).toLocaleTimeString()}
                    </Text>
                )}
            >
                <Table
                    dataSource={stagedMissions}
                    rowKey={(record: any) => String(record.stagedMissionId || record.operationMissionId || record.missionId)}
                    size="small"
                    pagination={false}
                    locale={{ emptyText: 'No staged missions reported by this robot' }}
                    onRow={(record: any) => {
                        const detailPath = missionDetailPath(record.missionId);
                        return detailPath ? {
                            className: 'operations-clickable-row',
                            onClick: () => navigate(detailPath),
                        } : {};
                    }}
                    columns={[
                        {
                            title: 'Mission',
                            key: 'mission',
                            render: (_, record: any) => {
                                const detailPath = missionDetailPath(record.missionId);
                                const label = record.missionName || record.name || `Mission ${record.missionId || ''}`;
                                return (
                                <Space direction="vertical" size={0}>
                                    {detailPath ? (
                                        <Button
                                            type="link"
                                            size="small"
                                            className="operations-inline-link"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                navigate(detailPath);
                                            }}
                                        >
                                            {label}
                                        </Button>
                                    ) : (
                                        <Text strong>{label}</Text>
                                    )}
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        Mission #{record.missionId ?? '—'} · Operation #{record.operationId ?? '—'} · Step #{record.operationMissionId ?? '—'}
                                    </Text>
                                </Space>
                                );
                            },
                        },
                        {
                            title: 'Schedule',
                            key: 'schedule',
                            render: (_, record: any) => {
                                const scheduledAt = record.operationScheduledStartAt || record.scheduledStartAt || record.scheduledAt;
                                return scheduledAt ? new Date(scheduledAt).toLocaleString() : <Text type="secondary">—</Text>;
                            },
                        },
                        {
                            title: 'State',
                            key: 'state',
                            render: (_, record: any) => <Tag>{record.state || record.status || 'staged'}</Tag>,
                        },
                        {
                            title: 'Points',
                            key: 'points',
                            render: (_, record: any) => record.pointCount ?? record.waypointCount ?? <Text type="secondary">—</Text>,
                        },
                        {
                            title: 'Action',
                            key: 'action',
                            align: 'right' as const,
                            render: (_, record: any) => {
                                const state = String(record.state || record.status || 'staged').toLowerCase();
                                const terminal = ['running', 'completed', 'failed', 'aborted', 'cancelled', 'canceled'].includes(state);
                                const payload = {
                                    stagedMissionId: record.stagedMissionId,
                                    missionId: record.missionId,
                                    operationId: record.operationId,
                                    operationMissionId: record.operationMissionId,
                                };
                                return (
                                    <span onClick={(event) => event.stopPropagation()}>
                                    <Popconfirm
                                        title="Start staged mission"
                                        description="Start this staged mission on the robot now?"
                                        onConfirm={() => sendCommand('start_mission', payload, {
                                            missionId: record.missionId,
                                            operationId: record.operationId,
                                        })}
                                        okText="Start"
                                        cancelText="Cancel"
                                    >
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<PlayCircleOutlined />}
                                            loading={sendingCommand}
                                            disabled={terminal || Boolean(activeMission || statusSnapshot?.activeMission)}
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            Start
                                        </Button>
                                    </Popconfirm>
                                    </span>
                                );
                            },
                        },
                    ]}
                />
            </Card>

            {/* ─── Row 4: Status & Quick Actions ─── */}
            <Card size="small" title={<><ThunderboltOutlined /> Status &amp; Quick Actions</>} style={{ marginBottom: 16 }}>
                <Row gutter={[16, 8]} align="middle">
                    <Col xs={24} sm={12}>
                        <Space direction="vertical" size={4}>
                            {battery !== undefined && (
                                <Space>
                                    <Text style={{ fontSize: 12 }}>🔋 Battery:</Text>
                                    <Progress
                                        percent={battery}
                                        size="small"
                                        style={{ width: 120 }}
                                        strokeColor={battery > 50 ? '#52c41a' : battery > 20 ? '#faad14' : '#ff4d4f'}
                                    />
                                </Space>
                            )}
                            {unitStatus && (
                                <Text style={{ fontSize: 12 }}>
                                    <ThunderboltOutlined /> Status: <Tag>{unitStatus}</Tag>
                                </Text>
                            )}
                            {activeMode && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Mode: <Tag>{activeMode}</Tag>
                                </Text>
                            )}
                            {manualOverride && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Manual override: <Tag color="orange">active</Tag>
                                </Text>
                            )}
                            {localizationLabel && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Localization: <Tag color={statusSnapshot?.localization?.ready ? 'green' : 'orange'}>{localizationLabel}</Tag>
                                </Text>
                            )}
                            {device.lastHeartbeat && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    <WifiOutlined /> Last heartbeat: {new Date(device.lastHeartbeat).toLocaleTimeString()}
                                </Text>
                            )}
                            {device.deviceType && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Type: {device.deviceType.name}
                                </Text>
                            )}
                        </Space>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Space wrap>
                            <Button
                                icon={<PauseCircleOutlined />}
                                size="small"
                                loading={sendingCommand}
                                disabled={!canPauseMission}
                                title={canPauseMission ? 'Pause current mission' : 'No active mission to pause'}
                                onClick={() => sendCommand('pause_mission')}
                            >
                                Pause Mission
                            </Button>
                            <Button
                                icon={<PlayCircleOutlined />}
                                size="small"
                                loading={sendingCommand}
                                disabled={!canResumeMission}
                                title={canResumeMission ? 'Resume paused mission' : 'No paused mission to resume'}
                                onClick={() => sendCommand('resume_mission')}
                            >
                                Resume Mission
                            </Button>
                            <Button
                                icon={<HomeOutlined />}
                                size="small"
                                type="primary"
                                ghost
                                loading={sendingCommand}
                                onClick={() => sendCommand('return_home')}
                            >
                                Return Home
                            </Button>
                            <Button
                                icon={<CameraOutlined />}
                                size="small"
                                loading={sendingCommand}
                                onClick={() => sendCommand('capture')}
                            >
                                Capture
                            </Button>
                            <Button
                                icon={<StopOutlined />}
                                size="small"
                                danger
                                loading={sendingCommand}
                                onClick={() => sendCommand('emergency_stop')}
                            >
                                E-Stop
                            </Button>
                            <Popconfirm
                                title="Cancel Mission"
                                description="Are you sure you want to cancel the current mission?"
                                onConfirm={() => sendCommand('abort_mission')}
                                okText="Yes, cancel"
                                cancelText="No"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    icon={<CloseCircleOutlined />}
                                    size="small"
                                    danger
                                    type="primary"
                                    loading={sendingCommand}
                                    disabled={!canCancelMission}
                                    title={canCancelMission ? 'Cancel current mission' : 'No active mission to cancel'}
                                >
                                    Cancel Mission
                                </Button>
                            </Popconfirm>
                        </Space>
                    </Col>
                </Row>
                {moduleStatusRows.length > 0 && (
                    <Table
                        style={{ marginTop: 12 }}
                        dataSource={moduleStatusRows}
                        rowKey="key"
                        size="small"
                        pagination={false}
                        columns={[
                            {
                                title: 'Module',
                                dataIndex: 'name',
                                key: 'name',
                            },
                            {
                                title: 'Runtime',
                                dataIndex: 'status',
                                key: 'status',
                                render: (v: string) => <Tag>{v}</Tag>,
                            },
                            {
                                title: 'Health',
                                dataIndex: 'health',
                                key: 'health',
                                render: (v: string | null) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
                            },
                            {
                                title: 'Message',
                                dataIndex: 'message',
                                key: 'message',
                                render: (v: string | null) => v || <Text type="secondary">—</Text>,
                            },
                        ]}
                    />
                )}
            </Card>

            {/* ─── Row 4: Device Module Configuration ─── */}
            <Card
                size="small"
                title={<><SettingOutlined /> Device Module Configuration</>}
                style={{ marginBottom: 16 }}
                extra={
                    <Button
                        type="primary"
                        size="small"
                        icon={<SaveOutlined />}
                        loading={savingModules}
                        onClick={saveModules}
                    >
                        Save modules
                    </Button>
                }
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Per-device module settings"
                    description="These settings belong to this physical device. Changing depth, thresholds, lightbox sequences, or sensor calibration here does not change the shared module catalogue."
                />
                <Form form={moduleForm} layout="vertical">
                    <ModuleFormList
                        form={moduleForm}
                        moduleDefinitions={moduleDefinitions}
                        name="modules"
                        title="module"
                        emptyDescription="This device has no physical modules configured yet."
                        visualOnly
                        showMetadata={false}
                    />
                </Form>
            </Card>

            {/* ─── Row 5: Command Queue ─── */}
            <Card size="small" title={<><SendOutlined /> Command Queue</>}>
                <Table
                    dataSource={commands}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    locale={{ emptyText: 'No recent commands' }}
                    columns={[
                        {
                            title: 'Command',
                            dataIndex: 'commandType',
                            key: 'commandType',
                            render: (v: string) => <Tag>{v}</Tag>,
                        },
                        {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (v: string) => <Tag color={commandStatusColor(v)}>{v}</Tag>,
                        },
                        {
                            title: 'Created',
                            dataIndex: 'createdAt',
                            key: 'createdAt',
                            render: (v: string) => v ? new Date(v).toLocaleTimeString() : '—',
                        },
                        {
                            title: 'Executed',
                            dataIndex: 'executedAt',
                            key: 'executedAt',
                            render: (v: string) => v ? new Date(v).toLocaleTimeString() : '—',
                        },
                    ]}
                />
            </Card>
        </div>
    );
};

export default DeviceDetailView;
